import {
  Button,
  DataTable,
  FlexBox,
  IconButton,
  Page,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { isResponseWithData } from 'lib-shared-auth';
import { useMemo } from 'react';

import { queuesApi } from 'apis/queues';
import { trustpilotApi } from 'apis/trustpilot/trustpilot.api';
import { appActions } from 'app/app.actions';
import { AppLink } from 'components/appLink';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';

export const ReviewsList = (): JSX.Element => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const notify = useNotifications();
  const { integrations, isFetching, isError, refetch } =
    trustpilotApi.useReadTrustpilotIntegrationsQuery(undefined, {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        integrations: data?.data.integrations ?? [],
      }),
    });

  const queueIds = useMemo(() => {
    const ids: Set<string> = new Set();
    integrations.forEach(({ default_queue_id }) => {
      if (default_queue_id) ids.add(default_queue_id);
    });

    return Array.from(ids);
  }, [integrations]);

  const { queues } = queuesApi.useReadQueuesQuery(
    { queueIds, limit: queueIds.length },
    {
      skip: integrations.length === 0,
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        queues: data?.data.queues.reduce(
          (queues, queue) => Object.assign(queues, { [queue.id]: queue.name }),
          {},
        ),
      }),
    },
  );

  const [deleteTrustpilotIntegration] =
    trustpilotApi.useDeleteTrustpilotIntegrationMutation();

  const handleEdit = (id: string) => {
    navigate({
      path: '/settings/reviews/:id',
      params: { path: { id } },
    });
  };

  const handleDelete = async (id: string) => {
    const { isConfirmed, confirm } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: 'Delete review domain?',
            description: 'This review domain will be lost.',
            confirm: {
              label: 'Delete',
              async: true,
              asyncLabel: 'Deleting...',
            },
          },
        },
      }),
    );

    if (isConfirmed) {
      confirm(async () => {
        const response = await deleteTrustpilotIntegration({ id });
        if (isResponseWithData(response)) {
          notify(
            {
              title: 'Success',
              text: 'Review domain has been deleted.',
              icon: 'done',
              time: 5000,
            },
            NotificationType.Success,
          );
        } else {
          notify(
            {
              title: 'Error while deleting review domain.',
            },
            NotificationType.Error,
          );
        }
      });
    }
  };

  return (
    <Page.Section
      title="Trustpilot"
      controls={
        <AppLink path="/settings/reviews/create">
          <Button variant="primary">Create review domain</Button>
        </AppLink>
      }
    >
      <DataTable
        columns={[
          {
            label: 'Domain',
            selector: ({ identifying_domain }) => identifying_domain,
          },
          {
            label: 'Queue',
            selector: ({ default_queue_id }) =>
              default_queue_id ? queues?.[default_queue_id] : null,
          },
          {
            label: 'Filter threshold',
            selector: ({ score_filter }) =>
              score_filter?.max && score_filter.max < 5
                ? `${score_filter.max} ${
                    score_filter.max > 1 ? 'stars' : 'star'
                  } and below`
                : 'All reviews',
          },
          {
            label: 'Status',
            selector: ({ connected }) => (connected ? 'Enabled' : 'Disabled'),
          },
          {
            label: '',
            selector: ({ id }) => (
              <FlexBox alignItems="center" gap="100">
                <Tooltip content="Edit review domain">
                  <IconButton
                    icon="pen"
                    variant="tertiary"
                    onClick={() => handleEdit(id)}
                  />
                </Tooltip>
                <Tooltip content="Delete review domain">
                  <IconButton
                    icon="trash"
                    variant="tertiary"
                    onClick={() => handleDelete(id)}
                  />
                </Tooltip>
              </FlexBox>
            ),
            style: {
              width: 0,
            },
          },
        ]}
        data={integrations}
        tableStatusProps={{
          loadingFailed: isError,
          onLoadingRetry: refetch,
          loading: isFetching,
          emptyState:
            !isFetching && !isError && !integrations.length
              ? {
                  title: 'There are no Trustpilot review domains configured',
                  description: '',
                  icon: 'trustpilot',
                  iconColor: colors.green[500],
                }
              : undefined,
        }}
        tableStyleProps={{
          stickyHeader: true,
          stickyFooter: true,
        }}
      />
    </Page.Section>
  );
};
